import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Search,
  Filter,
  Calendar,
  Clock,
  User,
  FileText,
  Activity,
  AlertTriangle,
  Info,
  AlertCircle,
  XCircle,
  Eye,
  Download,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const levelIcons = {
  debug: Info,
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  critical: XCircle
};

const levelColors = {
  debug: 'bg-gray-100 text-gray-800',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
  critical: 'bg-red-200 text-red-900'
};

const actionLabels = (t) => ({
  login: t('audit.actions.login'),
  logout: t('audit.actions.logout'),
  login_failed: t('audit.actions.login_failed'),
  password_change: t('audit.actions.password_change'),
  create: t('audit.actions.create'),
  read: t('audit.actions.read'),
  update: t('audit.actions.update'),
  delete: t('audit.actions.delete'),
  assign: t('audit.actions.assign'),
  send: t('audit.actions.send'),
  receive: t('audit.actions.receive'),
  config_change: t('audit.actions.config_change'),
  request: t('audit.actions.request'),
  error: t('audit.actions.error'),
  // WhatsApp specific actions
  whatsapp_outgoing_text: 'WA: Message Sent',
  whatsapp_outgoing_template: 'WA: Template Sent',
  whatsapp_outgoing_location_request: 'WA: Location Request',
  whatsapp_outgoing_cta_url: 'WA: URL Button Sent',
  whatsapp_outgoing_list: 'WA: List Sent',
  whatsapp_outgoing_reply_buttons: 'WA: Buttons Sent',
  whatsapp_outgoing_location: 'WA: Location Sent',
  whatsapp_incoming_text: 'WA: Message Received',
  whatsapp_incoming_location: 'WA: Location Received',
  whatsapp_incoming_interactive: 'WA: Interaction Received',
  whatsapp_incoming_image: 'WA: Image Received',
  whatsapp_incoming_audio: 'WA: Audio Received',
  whatsapp_incoming_document: 'WA: Document Received',
  whatsapp_status_sent: 'WA: Status Sent',
  whatsapp_status_delivered: 'WA: Status Delivered',
  whatsapp_status_read: 'WA: Status Read'
});

const LogDetail = ({ log, onClose, t }) => {
  const labels = actionLabels(t);
  if (!log) return null;
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.dateTime')}</Label>
          <p className="font-medium">
            {new Date(log.timestamp).toLocaleString()}
          </p>
        </div>
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.level')}</Label>
          <div className="mt-1">
            <Badge className={levelColors[log.level]}>
              {log.level?.toUpperCase()}
            </Badge>
          </div>
        </div>
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.action')}</Label>
          <p className="font-medium">{labels[log.action] || log.action}</p>
        </div>
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.module')}</Label>
          <p className="font-medium capitalize">{log.module}</p>
        </div>
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.user')}</Label>
          <p className="font-medium">{log.user_email || t('audit.detail.system')}</p>
        </div>
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.ip')}</Label>
          <p className="font-medium font-mono text-sm">{log.ip_address || 'N/A'}</p>
        </div>
      </div>
      
      <div>
        <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.description')}</Label>
        <p className="mt-1">{log.description}</p>
      </div>
      
      {log.entity_type && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.entityType')}</Label>
            <p className="font-medium">{log.entity_type}</p>
          </div>
          <div>
            <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.entityId')}</Label>
            <p className="font-medium font-mono text-sm">{log.entity_id}</p>
          </div>
        </div>
      )}
      
      {log.duration_ms !== undefined && (
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.duration')}</Label>
          <p className="font-medium">{log.duration_ms}ms</p>
        </div>
      )}
      
      {log.old_value && (
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.oldValue')}</Label>
          <pre className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs overflow-x-auto">
            {JSON.stringify(log.old_value, null, 2)}
          </pre>
        </div>
      )}
      
      {log.new_value && (
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.newValue')}</Label>
          <pre className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs overflow-x-auto">
            {JSON.stringify(log.new_value, null, 2)}
          </pre>
        </div>
      )}
      
      {log.metadata && Object.keys(log.metadata).length > 0 && (
        <div>
          <Label className="text-gray-500 dark:text-gray-400">{t('audit.detail.metadata')}</Label>
          <pre className="mt-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs overflow-x-auto">
            {JSON.stringify(log.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export const AuditPage = () => {
  const { t } = useTranslation('security');
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const totalPages = Math.ceil(totalCount / pageSize);

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchFilters();
  }, [levelFilter, moduleFilter, actionFilter, currentPage, pageSize]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [levelFilter, moduleFilter, actionFilter, search]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', ((currentPage - 1) * pageSize).toString());
      params.append('limit', pageSize.toString());
      if (levelFilter && levelFilter !== 'all') params.append('level', levelFilter);
      if (moduleFilter && moduleFilter !== 'all') params.append('module', moduleFilter);
      if (actionFilter && actionFilter !== 'all') params.append('action', actionFilter);
      if (search) params.append('search', search);
      
      // Fetch logs and count in parallel
      const [logsResponse, countResponse] = await Promise.all([
        axios.get(`${API_URL}/api/audit/logs?${params.toString()}`),
        axios.get(`${API_URL}/api/audit/logs/count?${new URLSearchParams({
          ...(levelFilter && levelFilter !== 'all' && { level: levelFilter }),
          ...(moduleFilter && moduleFilter !== 'all' && { module: moduleFilter }),
          ...(actionFilter && actionFilter !== 'all' && { action: actionFilter })
        }).toString()}`)
      ]);
      
      setLogs(logsResponse.data);
      setTotalCount(countResponse.data.count || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/audit/stats?days=7`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFilters = async () => {
    try {
      const [modulesRes, actionsRes] = await Promise.all([
        axios.get(`${API_URL}/api/audit/logs/modules`),
        axios.get(`${API_URL}/api/audit/logs/actions`)
      ]);
      setModules(modulesRes.data.modules || []);
      setActions(actionsRes.data.actions || []);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLogs();
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setIsDetailOpen(true);
  };

  const labels = actionLabels(t);

  return (
    <div className="space-y-6" data-testid="audit-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-['Work_Sans',sans-serif]">
            {t('audit.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('audit.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={() => { setLoading(true); fetchLogs(); fetchStats(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          {t('audit.refresh')}
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('audit.stats.info')}</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.by_level?.info || 0}</p>
                </div>
                <Info className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('audit.stats.warning')}</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.by_level?.warning || 0}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-yellow-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('audit.stats.error')}</p>
                  <p className="text-2xl font-bold text-red-600">{stats.by_level?.error || 0}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('audit.stats.totalToday')}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {Object.values(stats.by_level || {}).reduce((a, b) => a + b, 0)}
                  </p>
                </div>
                <Activity className="h-8 w-8 text-gray-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('audit.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
                data-testid="audit-search"
              />
            </div>
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-full lg:w-40" data-testid="audit-level-filter">
                <SelectValue placeholder={t('audit.filters.level')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('audit.filters.allLevels')}</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <Select value={moduleFilter} onValueChange={setModuleFilter}>
              <SelectTrigger className="w-full lg:w-40" data-testid="audit-module-filter">
                <SelectValue placeholder={t('audit.filters.module')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('audit.filters.allModules')}</SelectItem>
                {modules.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-full lg:w-40" data-testid="audit-action-filter">
                <SelectValue placeholder={t('audit.filters.action')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('audit.filters.allActions')}</SelectItem>
                {actions.map((a) => (
                  <SelectItem key={a} value={a}>{labels[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} data-testid="audit-search-btn">
              {t('audit.search')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-testid="audit-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.dateTime')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.level')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.action')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.module')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.user')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.description')}</th>
                    <th className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('audit.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        {t('audit.table.noLogs')}
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => {
                      const LevelIcon = levelIcons[log.level] || Info;
                      return (
                        <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {new Date(log.timestamp).toLocaleDateString()}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-400">
                              <Clock className="h-3 w-3" />
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge className={`${levelColors[log.level]} hover:${levelColors[log.level]}`}>
                              <LevelIcon className="h-3 w-3 mr-1" />
                              {log.level}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            {labels[log.action] || log.action}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge variant="outline" className="capitalize">
                              {log.module}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {log.user_email || t('audit.detail.system')}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate">
                            {log.description}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetail(log)}
                              data-testid={`view-log-${log.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            <span>
              {t('audit.pagination.showing')} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} {t('audit.pagination.of')} {totalCount} {t('audit.pagination.records')}
            </span>
            <div className="flex items-center gap-2">
              <span>{t('audit.pagination.show')}:</span>
              <Select value={pageSize.toString()} onValueChange={(val) => { setPageSize(parseInt(val)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              data-testid="pagination-first"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              data-testid="pagination-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => goToPage(pageNum)}
                    className={`w-8 ${currentPage === pageNum ? 'bg-[#E63946] hover:bg-[#d32836]' : ''}`}
                    data-testid={`pagination-page-${pageNum}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              data-testid="pagination-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              data-testid="pagination-last"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Log Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('audit.detail.title')}</DialogTitle>
          </DialogHeader>
          <LogDetail log={selectedLog} onClose={() => setIsDetailOpen(false)} t={t} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditPage;
